import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getBaseUrl } from "../utils";
import ExamAlertDetailModal, { formatDate } from "./ExamAlertDetailModal";

const PAGE_SIZE = 10;
const FALLBACK_IMAGE = "/assates/Logo__newimage-removebg-preview.png";

const NotificationPage = () => {
    const baseUrl = getBaseUrl();
    const [notifications, setNotifications] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);

    const fetchNotifications = useCallback(
        async (pageToLoad) => {
            setLoading(true);
            setError("");
            try {
                const token = localStorage.getItem("userToken");
                const response = await axios.get(`${baseUrl}user_api/notificationList`, {
                    params: { page: pageToLoad, limit: PAGE_SIZE },
                    headers: { Authorization: `Bearer ${token}` },
                });
                const results = response.data?.results || [];
                setNotifications((prev) => (pageToLoad === 1 ? results : [...prev, ...results]));
                setTotalPages(response.data?.pagination?.totalPages || 1);
                setPage(pageToLoad);
            } catch (err) {
                console.error("Error fetching notifications:", err);
                setError("Could not load notifications. Please try again.");
            } finally {
                setLoading(false);
            }
        },
        [baseUrl]
    );

    useEffect(() => {
        fetchNotifications(1);
    }, [fetchNotifications]);

    const examInfoOf = (n) => (n.examNotificationInfo && n.examNotificationInfo._id ? n.examNotificationInfo : null);

    return (
        <div className="p-4 md:p-6">
            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

            {!loading && notifications.length === 0 && !error ? (
                <p className="text-gray-600">No notifications available</p>
            ) : (
                <div className="flex flex-col gap-4">
                    {notifications.map((n) => {
                        const exam = examInfoOf(n);
                        return (
                            <button
                                key={n._id}
                                type="button"
                                onClick={() => setSelected(n)}
                                className="w-full text-left bg-[#F9F9F9] hover:bg-[#F2F2F2] rounded-lg p-4 flex items-start gap-4 transition focus:outline-none focus:ring-2 focus:ring-[#28282B]"
                            >
                                <img
                                    src={n.image ? String(n.image).replace(/\\/g, "/") : FALLBACK_IMAGE}
                                    alt=""
                                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover shrink-0 bg-white"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = FALLBACK_IMAGE;
                                    }}
                                />
                                <div className="min-w-0 flex-1">
                                    <h3 className="text-base md:text-lg font-bold m-0 break-words">
                                        {n.title || "Notification"}
                                    </h3>
                                    {exam?.course_or_exam && (
                                        <p className="text-sm font-semibold text-gray-800 m-0 mt-1">{exam.course_or_exam}</p>
                                    )}
                                    {n.description && (
                                        <p className="text-sm text-gray-600 m-0 mt-1 line-clamp-2">{n.description}</p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                                        {n.Created_date && <span>{formatDate(n.Created_date)}</span>}
                                        <span className="font-semibold text-[#0053C0]">
                                            View details <i className="ri-arrow-right-s-line align-middle"></i>
                                        </span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {loading && <p className="text-gray-500 text-sm mt-4">Loading...</p>}

            {!loading && page < totalPages && (
                <div className="flex justify-center mt-6">
                    <button
                        type="button"
                        onClick={() => fetchNotifications(page + 1)}
                        className="px-5 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50"
                    >
                        Load more
                    </button>
                </div>
            )}

            {selected && (
                <ExamAlertDetailModal
                    alert={examInfoOf(selected)}
                    fallback={{
                        title: selected.title,
                        description: selected.description,
                        image: selected.image,
                        Created_date: selected.Created_date,
                    }}
                    onClose={() => setSelected(null)}
                />
            )}
        </div>
    );
};

export default NotificationPage;
